import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { differenceInMinutes } from "date-fns";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { toast } from "react-toastify";

import { getErrorMessageFromResponse } from "@src/common/helpers/getErrorMessageFromResponse";
import {
  INSUFFICIENT_SUBSCRIPTION,
  LOGIN,
  OTHER_ERROR,
} from "@src/common/helpers/toastIds";
import {
  deleteUserToken,
  getTimeToExpire,
  getUserToken,
} from "@src/common/helpers/userToken";
import useLogout from "@src/common/hooks/useLogout";
import useRefreshToken from "@src/common/hooks/useRefreshToken";
import { AxiosContext } from "@src/config/axios/AxiosContext";
import { API_URL } from "@src/config/baseUrls";
import { activationCodeId, profileButtonId } from "@src/config/consts";
import { LoginState } from "@src/modules/Public/shared/LoginForm/LoginForm";
import routesPaths from "@src/modules/Routing/routesPaths";
import {
  ResponseWithErrors,
  ResponseWithMessage,
} from "@src/modules/shared/types";
import { getImpersonateUser } from "@src/common/helpers/impersonatedUser";

export const baseAxiosHeaders = {
  "Content-Type": "application/json",
};

export const baseAxiosConfig: AxiosRequestConfig = {
  withCredentials: true,
  baseURL: API_URL,
};

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

const AxiosProvider = ({ children }: React.PropsWithChildren<unknown>) => {
  const { i18n, t } = useTranslation();
  const refreshToken = useRefreshToken();
  const history = useHistory<LoginState>();
  const { logout } = useLogout();

  const axiosValue = useMemo(() => {
    let isRefreshing = false;
    let failedQueue: Array<{
      resolve: (value?: unknown) => void;
      reject: (reason?: any) => void;
    }> = [];

    const processQueue = (error: any, token: string | null = null) => {
      failedQueue.forEach((prom) => {
        if (error) {
          prom.reject(error);
        } else {
          prom.resolve(token);
        }
      });
      failedQueue = [];
    };

    const axiosInstance = axios.create({
      headers: {
        ...baseAxiosHeaders,
      },
      ...baseAxiosConfig,
    });

    axiosInstance.interceptors.request.use(async (config) => {
      const userToken = getUserToken();
      if (userToken !== null && !isRefreshing) {
        const validDuration = differenceInMinutes(
          userToken.expiresAt,
          userToken.issuedAt,
        );
        const diff = getTimeToExpire(userToken);
        if (diff < validDuration / 2) {
          isRefreshing = true;
          try {
            await refreshToken();
          } catch (error) {
            console.error("Unable to refresh token", error);
            logout();
          } finally {
            isRefreshing = false;
          }
        }
      }

      const freshUserToken = getUserToken();
      if (freshUserToken && config.headers) {
        config.headers.Authorization = `Bearer ${freshUserToken.token}`;
      }

      if (config.headers) {
        config.headers["Accept-Language"] = i18n.language;
        const impersonateUser = getImpersonateUser();
        if (impersonateUser !== null) {
          config.headers["X-Impersonate-UserId"] = impersonateUser.userId;
        }
      }

      return config;
    });

    axiosInstance.interceptors.response.use(
      undefined,
      async (error: AxiosError<ResponseWithMessage & ResponseWithErrors>) => {
        const originalRequest = error.config as CustomAxiosRequestConfig;

        if (error.response?.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            error.response.data = JSON.parse(text);
          } catch {}
        }

        let errorMsg;
        switch (error.response?.status) {
          case 401:
            if (originalRequest._retry) {
              return Promise.reject(error);
            }

            if (isRefreshing) {
              return new Promise(function (resolve, reject) {
                failedQueue.push({ resolve, reject });
              })
                .then((token) => {
                  if (originalRequest.headers) {
                    originalRequest.headers[
                      "Authorization"
                    ] = `Bearer ${token}`;
                  }
                  return axiosInstance(originalRequest);
                })
                .catch((err) => {
                  return Promise.reject(err);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
              await refreshToken();
              const token = getUserToken()?.token;
              if (!token) throw new Error("No token after refresh");

              processQueue(null, token);

              if (originalRequest.headers) {
                originalRequest.headers["Authorization"] = `Bearer ${token}`;
              }
              return axiosInstance(originalRequest);
            } catch (error) {
              processQueue(error, null);
              console.error(error);
              deleteUserToken();
              history.replace(routesPaths.LOGIN);
              toast.error(t("Please log in"), { toastId: LOGIN });

              return Promise.reject(error);
            } finally {
              isRefreshing = false;
            }
            break;
          case 402:
          case 403:
            errorMsg =
              error.response.data.message ??
              t(
                "Your subscription doesn't allow that operation. Please activate appropriate plan",
              );
            toast.error(errorMsg, {
              toastId: INSUFFICIENT_SUBSCRIPTION,
              autoClose: 3500,
            });

            document.getElementById(profileButtonId)?.click();
            // NOTE: Wait for profile data to load
            setTimeout(
              () => document.getElementById(activationCodeId)?.focus(),
              2000,
            );
            break;
          case 409:
            errorMsg = error.response.data.message;
            toast.success(errorMsg);
            break;
          case 413:
            toast.error(t("File is too large"), { autoClose: 3500 });
            break;
          default:
            toast.error(
              getErrorMessageFromResponse(error) ??
                t("Something went wrong, please try again"),
              { toastId: OTHER_ERROR, autoClose: 3500 },
            );
            break;
        }

        return Promise.reject(error);
      },
    );

    return axiosInstance;
  }, [i18n.language, refreshToken, logout, t, history]);

  return (
    <AxiosContext.Provider value={axiosValue}>{children}</AxiosContext.Provider>
  );
};

export default AxiosProvider;
